import { useState, useMemo, useCallback, useEffect } from 'react';
import { DndContext, DragStartEvent, DragEndEvent, DragOverlay, PointerSensor, useSensor, useSensors, useDroppable, useDraggable } from '@dnd-kit/core';
import { CandidateStatus, CandidateSummary, PipelineConfig, PipelineStage } from '@recruitai/shared';
import { toast } from 'sonner';
import { CandidateCard } from './CandidateCard';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';

export interface PipelineKanbanBoardProps {
  jobId: string;
  candidates: CandidateSummary[];
  pipelineConfig: PipelineConfig;
  isReadOnly: boolean;
  onStageChange: (candidateId: string, toStageId: string) => Promise<boolean>;
  onCandidateClick: (candidateId: string) => void;
}

const STAGE_COLORS = [
  'bg-yellow-100', 'bg-orange-100', 'bg-blue-100', 'bg-purple-100',
  'bg-pink-100', 'bg-teal-100', 'bg-indigo-100', 'bg-cyan-100',
  'bg-lime-100', 'bg-amber-100', 'bg-green-100', 'bg-emerald-100',
  'bg-sky-100', 'bg-violet-100', 'bg-fuchsia-100', 'bg-rose-100',
  'bg-red-100',
];

function getAdjacentStageIds(stages: PipelineStage[], currentStageId: string): string[] {
  const idx = stages.findIndex(s => s.stageId === currentStageId);
  if (idx === -1) return [];
  const targets: string[] = [];
  if (idx > 0) targets.push(stages[idx - 1].stageId);
  if (idx < stages.length - 1) targets.push(stages[idx + 1].stageId);
  targets.push('rejected'); // rejection always allowed
  return targets;
}

export function PipelineKanbanBoard({
  jobId: _jobId,
  candidates,
  pipelineConfig,
  isReadOnly,
  onStageChange,
  onCandidateClick,
}: PipelineKanbanBoardProps) {
  const { user } = useAuth();
  const stages = pipelineConfig.stages;
  const [localCandidates, setLocalCandidates] = useState<CandidateSummary[]>(candidates);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [activeDragSourceStage, setActiveDragSourceStage] = useState<string | null>(null);

  useEffect(() => { setLocalCandidates(candidates); }, [candidates]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Group candidates by pipelineStageId (fallback to legacy status mapping)
  const groupedByStage = useMemo(() => {
    const groups: Record<string, CandidateSummary[]> = {};
    for (const stage of stages) groups[stage.stageId] = [];
    groups['rejected'] = [];

    for (const c of localCandidates) {
      const stageId = (c as any).pipelineStageId || mapLegacyToStageId(c.status, stages);
      if (c.status === CandidateStatus.REJECTED) {
        groups['rejected'].push(c);
      } else if (groups[stageId]) {
        groups[stageId].push(c);
      } else {
        // fallback to first stage
        groups[stages[0]?.stageId]?.push(c);
      }
    }
    // Sort by fitScore desc
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => (b.fitScore ?? 0) - (a.fitScore ?? 0));
    }
    return groups;
  }, [localCandidates, stages]);

  const validTargets = useMemo(
    () => activeDragSourceStage ? getAdjacentStageIds(stages, activeDragSourceStage) : [],
    [activeDragSourceStage, stages]
  );

  const activeDragCandidate = useMemo(
    () => activeDragId ? localCandidates.find(c => c.candidateId === activeDragId) : null,
    [activeDragId, localCandidates]
  );

  const moveCandidate = useCallback(async (candidateId: string, toStageId: string) => {
    const candidate = localCandidates.find(c => c.candidateId === candidateId);
    if (!candidate) return;

    const fromStageId = (candidate as any).pipelineStageId || mapLegacyToStageId(candidate.status, stages);
    const valid = getAdjacentStageIds(stages, fromStageId);
    if (!valid.includes(toStageId)) {
      toast.error('Transição não permitida. Apenas etapas adjacentes.');
      return;
    }

    // Optimistic update
    const prev = [...localCandidates];
    setLocalCandidates(cs => cs.map(c =>
      c.candidateId === candidateId ? { ...c, pipelineStageId: toStageId } as any : c
    ));

    const success = await onStageChange(candidateId, toStageId);
    if (!success) {
      setLocalCandidates(prev);
      toast.error('Erro ao mover candidato. Tente novamente.');
    }
  }, [localCandidates, stages, onStageChange]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const id = event.active.id as string;
    const stageId = event.active.data.current?.stageId as string;
    setActiveDragId(id);
    setActiveDragSourceStage(stageId);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);
    setActiveDragSourceStage(null);
    if (!over) return;
    const candidateId = active.id as string;
    const targetStageId = over.id as string;
    const sourceStageId = active.data.current?.stageId as string;
    if (sourceStageId === targetStageId) return;
    moveCandidate(candidateId, targetStageId);
  }, [moveCandidate]);

  const isCurrentUserOwner = (stage: PipelineStage) =>
    user?.userId ? stage.owners.some(o => o.userId === user.userId) : false;

  const rejectedCandidates = groupedByStage['rejected'] || [];

  return (
    <div>
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4">
          {stages.map((stage, idx) => {
            let isValidTarget: boolean | null = null;
            if (activeDragId) {
              isValidTarget = validTargets.includes(stage.stageId);
            }
            const isOwner = isCurrentUserOwner(stage);

            return (
              <div key={stage.stageId} className={`flex flex-col min-w-[280px] w-[280px] rounded-lg border-2 transition-all ${
                isValidTarget === true ? (activeDragSourceStage === stage.stageId ? 'border-transparent' : 'border-primary/50') :
                isValidTarget === false ? 'opacity-50 border-transparent' : 'border-transparent'
              } ${isOwner ? 'ring-2 ring-blue-300' : ''}`}>
                {/* Header */}
                <div ref={undefined} className={`${STAGE_COLORS[idx % STAGE_COLORS.length]} rounded-t-lg px-3 py-2`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">{stage.name}</span>
                    <span className="bg-white/80 text-xs font-bold px-2 py-0.5 rounded-full">
                      {groupedByStage[stage.stageId]?.length || 0}
                    </span>
                  </div>
                  {stage.owners.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {stage.owners.map(o => (
                        <Badge key={o.userId} variant="secondary" className="text-[10px] px-1 py-0">
                          {o.userName}{o.contextualRole ? ` · ${o.contextualRole}` : ''}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Droppable area */}
                <DroppableColumn stageId={stage.stageId}>
                  {(groupedByStage[stage.stageId] || []).map(c => (
                    <DraggableCandidateCard
                      key={c.candidateId}
                      candidate={c}
                      stageId={stage.stageId}
                      isReadOnly={isReadOnly}
                      onCandidateClick={onCandidateClick}
                      onQuickReject={(id) => moveCandidate(id, 'rejected')}
                      onQuickAdvance={(id) => {
                        const nextStage = stages[idx + 1];
                        if (nextStage) moveCandidate(id, nextStage.stageId);
                      }}
                    />
                  ))}
                  {(groupedByStage[stage.stageId] || []).length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">Nenhum candidato</p>
                  )}
                </DroppableColumn>
              </div>
            );
          })}
        </div>

        <DragOverlay>
          {activeDragCandidate ? (
            <CandidateCard
              candidate={activeDragCandidate}
              isReadOnly
              isDragging
              onCandidateClick={() => {}}
              onQuickReject={() => {}}
              onQuickAdvance={() => {}}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Rejected section */}
      {rejectedCandidates.length > 0 && (
        <div className="mt-4 border rounded-lg p-3">
          <h3 className="text-sm font-semibold text-red-600 mb-2">
            Rejeitados ({rejectedCandidates.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {rejectedCandidates.map(c => (
              <CandidateCard
                key={c.candidateId}
                candidate={c}
                isReadOnly
                onCandidateClick={onCandidateClick}
                onQuickReject={() => {}}
                onQuickAdvance={() => {}}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper: map legacy CandidateStatus to a pipeline stageId
function mapLegacyToStageId(status: CandidateStatus, stages: PipelineStage[]): string {
  const match = stages.find(s => s.legacyStatus === status);
  return match?.stageId || stages[0]?.stageId || '';
}

// Droppable column wrapper
function DroppableColumn({ stageId, children }: { stageId: string; children: React.ReactNode }) {
  const { setNodeRef } = useDroppable({ id: stageId });
  return (
    <div ref={setNodeRef} className="flex-1 overflow-y-auto p-2 max-h-[calc(100vh-280px)] bg-gray-50/50">
      {children}
    </div>
  );
}

// Draggable candidate card wrapper
function DraggableCandidateCard({ candidate, stageId, isReadOnly, onCandidateClick, onQuickReject, onQuickAdvance }: {
  candidate: CandidateSummary;
  stageId: string;
  isReadOnly: boolean;
  onCandidateClick: (id: string) => void;
  onQuickReject: (id: string) => void;
  onQuickAdvance: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: candidate.candidateId,
    data: { stageId },
    disabled: isReadOnly,
  });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)` } : undefined;

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <CandidateCard
        candidate={candidate}
        isReadOnly={isReadOnly}
        onCandidateClick={onCandidateClick}
        onQuickReject={onQuickReject}
        onQuickAdvance={onQuickAdvance}
      />
    </div>
  );
}
